/**
 * useLayoutData — fetches all data needed to render a LayoutPage.
 *
 * 1. Fetches categories for the given contentType from the oneadvanced API.
 * 2. Fetches items for each category in parallel.
 * 3. Batch-fetches all referenced entities from the Backstage catalog.
 */

import { useState, useEffect } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import type { Entity } from '@backstage/catalog-model';
import { oneadvancedApiRef } from '../../../api/OneAdvancedApi';
import type { ContentType, LayoutCategory, LayoutItem, LayoutSection } from '../types';

export interface LayoutData {
  loading: boolean;
  error: Error | undefined;
  sections: LayoutSection[];
  categories: LayoutCategory[];
  itemsByCategory: Record<string, LayoutItem[]>;
  entityByRef: Record<string, Entity>;
  reload: () => void;
}

export function useLayoutData(contentType: ContentType): LayoutData {
  const api = useApi(oneadvancedApiRef);
  const catalogApi = useApi(catalogApiRef);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [sections, setSections] = useState<LayoutSection[]>([]);
  const [categories, setCategories] = useState<LayoutCategory[]>([]);
  const [itemsByCategory, setItemsByCategory] = useState<Record<string, LayoutItem[]>>({});
  const [entityByRef, setEntityByRef] = useState<Record<string, Entity>>({});
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(undefined);
      try {
        // 1. Fetch sections and categories in parallel
        const [secs, cats] = await Promise.all([
          api.layouts.getSections(contentType),
          api.layouts.getCategories(contentType),
        ]);
        if (cancelled) return;

        // 2. Fetch items for each category in parallel
        const itemResults = await Promise.all(
          cats.map(cat => api.layouts.getCategoryItems(contentType, cat.id)),
        );
        if (cancelled) return;

        const byCategory: Record<string, LayoutItem[]> = {};
        cats.forEach((cat, i) => {
          byCategory[cat.id] = itemResults[i];
        });

        // 3. Collect all unique entity refs
        const allRefs = Array.from(
          new Set(itemResults.flat().map(item => item.entityRef)),
        );

        // 4. Batch-fetch entities from catalog
        const byRef: Record<string, Entity> = {};
        if (allRefs.length > 0) {
          const result = await catalogApi.getEntitiesByRefs({ entityRefs: allRefs });
          if (cancelled) return;
          result.items.forEach(entity => {
            if (entity) {
              const ns = entity.metadata.namespace ?? 'default';
              const ref = `${entity.kind.toLowerCase()}:${ns}/${entity.metadata.name}`;
              byRef[ref] = entity;
              // Also index by the exact ref string from DB in case casing differs
            }
          });
          // Also map by original refs to be safe
          allRefs.forEach((ref, idx) => {
            if (result.items[idx]) {
              byRef[ref] = result.items[idx]!;
            }
          });
        }

        if (cancelled) return;
        setCategories(cats);
        setItemsByCategory(byCategory);
        setEntityByRef(byRef);
        setSections(secs);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e : new Error(String(e)));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [api, catalogApi, contentType, tick]);

  return {
    loading,
    error,
    sections,
    categories,
    itemsByCategory,
    entityByRef,
    reload: () => setTick(t => t + 1),
  };
}
